import { WritableComputedRef, computed, unref } from 'vue'
import { useRouter } from '../useRouter'
import type { MaybeRef } from '@vueuse/core'

export interface UseQueryOptions<T> {
    defaultValue?: MaybeRef<T>
    get?: (value: string) => T | undefined
    set?: (value: T | undefined) => string | undefined
    normalize?: (value?: T) => T | undefined
    validator?: (value?: T) => value is T
}

/**
 * Reactively get and set the URL search param with the given key.
 */
export function useQuery(key: string): WritableComputedRef<string>
export function useQuery<T>(
    key: string,
    opts: Partial<UseQueryOptions<T>>
): WritableComputedRef<T>
export function useQuery<T>(
    key: string,
    opts: Partial<UseQueryOptions<T>> = {}
) {
    const { state } = useRouter()
    const get = opts.get ?? (value => value as unknown as T)
    const validator = opts.validator ?? (_ => true)
    const set = opts.set ?? (value => String(value))
    const normalize = opts.normalize ?? (value => value)
    const defaultValue = opts.defaultValue

    function isNullish(value: unknown): value is null | undefined | '' {
        return value === null || value === undefined || value === ''
    }

    return computed<T | undefined>({
        get() {
            const value = state[key]?.toString().trim()

            const parsed = normalize(get(value))

            if (isNullish(parsed) || !validator(parsed)) {
                return unref(defaultValue)
            }

            return parsed
        },
        set(value) {
            const normalized = normalize(value)

            if (validator(normalized)) {
                const value = set(normalized)

                if (!isNullish(value)) {
                    return (state[key] = value)
                }
            }

            delete state[key]
        }
    })
}
